import { useState, useEffect, useCallback, useRef } from "react";
import config from "@/config";
import type {
  DashboardStatsQuery,
  FailReasonBreakdownStats,
  FailReasonDetailsStats,
} from "@/types";
import { buildStatsQueryParams } from "@/lib/dashboard-api";

type UseBreakdownOpts = {
  refetchKey?: number;
  /** When false, do not fetch (e.g. parent supplies bundle breakdown). */
  enabled?: boolean;
};

export function useFailReasonBreakdown(
  caseFilters: DashboardStatsQuery,
  options?: UseBreakdownOpts,
) {
  const refetchKey = options?.refetchKey ?? 0;
  const enabled = options?.enabled ?? true;
  const [data, setData] = useState<FailReasonBreakdownStats | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);

  const fetchSeqRef = useRef(0);

  const fetchData = useCallback(async () => {
    const seq = ++fetchSeqRef.current;
    setLoading(true);
    setError(null);
    try {
      const params = buildStatsQueryParams(caseFilters);
      const qs = params.toString();
      const res = await fetch(
        `${config.API_BASE_URL}/api/dashboard/fail-reason-breakdown${qs ? `?${qs}` : ""}`,
      );
      if (!res.ok) {
        const t = await res.text();
        throw new Error(t || `Request failed: ${res.status}`);
      }
      const json = (await res.json()) as FailReasonBreakdownStats;
      if (seq !== fetchSeqRef.current) return;
      setData(json);
    } catch (e) {
      if (seq !== fetchSeqRef.current) return;
      setError(
        e instanceof Error ? e.message : "Failed to load fail reason breakdown",
      );
    } finally {
      if (seq === fetchSeqRef.current) {
        setLoading(false);
      }
    }
  }, [caseFilters]);

  useEffect(() => {
    if (!enabled) {
      fetchSeqRef.current += 1;
      setLoading(false);
      return;
    }
    void fetchData();
  }, [enabled, fetchData, refetchKey]);

  return { data, loading, error, refresh: fetchData };
}

type UseDetailsOpts = {
  enabled: boolean;
  refetchKey?: number;
};

/**
 * Fetches fail_details distribution for a single fail_reason, same case filters
 * as the main breakdown.
 */
export function useFailReasonDetails(
  caseFilters: DashboardStatsQuery,
  failReason: string | null,
  options?: UseDetailsOpts,
) {
  const enabled = options?.enabled ?? false;
  const refetchKey = options?.refetchKey ?? 0;
  const [data, setData] = useState<FailReasonDetailsStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSeqRef = useRef(0);

  const fetchData = useCallback(async () => {
    const seq = ++fetchSeqRef.current;
    if (!failReason?.trim()) {
      if (seq === fetchSeqRef.current) {
        setData(null);
        setLoading(false);
      }
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const params = buildStatsQueryParams(caseFilters);
      params.set("fail_reason", failReason.trim());
      const qs = params.toString();
      const res = await fetch(
        `${config.API_BASE_URL}/api/dashboard/fail-reason-fail-details?${qs}`,
      );
      if (!res.ok) {
        const t = await res.text();
        throw new Error(t || `Request failed: ${res.status}`);
      }
      const json = (await res.json()) as FailReasonDetailsStats;
      if (seq !== fetchSeqRef.current) return;
      setData(json);
    } catch (e) {
      if (seq !== fetchSeqRef.current) return;
      setData(null);
      setError(
        e instanceof Error
          ? e.message
          : "Failed to load fail details breakdown",
      );
    } finally {
      if (seq === fetchSeqRef.current) {
        setLoading(false);
      }
    }
  }, [caseFilters, failReason]);

  useEffect(() => {
    if (!enabled || !failReason?.trim()) {
      fetchSeqRef.current += 1;
      setData(null);
      setError(null);
      setLoading(false);
      return;
    }
    void fetchData();
  }, [enabled, failReason, fetchData, refetchKey]);

  return { data, loading, error, refresh: fetchData };
}
